import { z } from 'zod';
/**
 * Catalogue list filters that `GET /api/cars` understands beyond the legacy
 * web parameters, shared by the API (which builds the Mongo match) and the
 * clients (which send the parameter). Additive and optional: a request without
 * them means exactly what it meant before.
 */
/** Query parameter: `closingToday=true` keeps only lots whose bidding closes today. */
export const CLOSING_TODAY_PARAM = 'closingToday';
/**
 * The calendar the word «сьогодні» refers to. The card's "Закривається
 * сьогодні" is computed on the device's local day; the audience is in Ukraine,
 * so the server — which has no device — uses Kyiv. ('Europe/Kiev' is the ICU
 * name every runtime ships; 'Europe/Kyiv' is an alias only newer ICU has.)
 */
export const CATALOGUE_DAY_TIME_ZONE = 'Europe/Kiev';
/** `closingToday` as it arrives in a query string: only the literal `true` turns it on. */
export const ClosingTodayParamSchema = z
    .string()
    .optional()
    .transform((value) => value === 'true');
/** Milliseconds `instant` is ahead of UTC in `timeZone` (DST-aware). */
function timeZoneOffsetMs(instant, timeZone) {
    const parts = new Intl.DateTimeFormat('en-US', {
        timeZone,
        hourCycle: 'h23',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
    }).formatToParts(new Date(instant));
    const get = (type) => Number(parts.find((part) => part.type === type)?.value ?? 0);
    const asUtc = Date.UTC(get('year'), get('month') - 1, get('day'), get('hour'), get('minute'), get('second'));
    return asUtc - (instant - (instant % 1000));
}
/**
 * The first instant of the NEXT calendar day in `timeZone`, i.e. the exclusive
 * end of the day `now` falls on there. DST-safe: the offset is re-read at the
 * candidate midnight itself, so the 23- and 25-hour days come out right.
 */
export function endOfDayInTimeZone(now, timeZone = CATALOGUE_DAY_TIME_ZONE) {
    const offsetNow = timeZoneOffsetMs(now.getTime(), timeZone);
    const local = new Date(now.getTime() + offsetNow);
    const nextMidnightAsUtc = Date.UTC(local.getUTCFullYear(), local.getUTCMonth(), local.getUTCDate() + 1);
    let instant = nextMidnightAsUtc - offsetNow;
    instant = nextMidnightAsUtc - timeZoneOffsetMs(instant, timeZone);
    return new Date(instant);
}
// ── «Ціна в Україні до» (landed-price filter, 2026-10-28) ─────────────────
/**
 * Query parameter: `landedMaxEur=8500` keeps only lots whose landed price in
 * Ukraine (bid + delivery + customs + commission, EUR, at the lot's current
 * bid) is at most this many euros. Served from the denormalised
 * `alpicar_cars.landed_total_eur_base` (see {@link LANDED_TOTAL_FIELD}); a lot
 * without a stored value (no bid yet) never qualifies.
 */
export const LANDED_MAX_EUR_PARAM = 'landedMaxEur';
/** The denormalised field the filter reads: the BASE-plan (planTier=null) landed total, whole EUR. */
export const LANDED_TOTAL_FIELD = 'landed_total_eur_base';
/** Which rule set (+ excise year) produced {@link LANDED_TOTAL_FIELD}; see {@link landedRulesVersionOf}. */
export const LANDED_RULES_VERSION_FIELD = 'landed_rules_version';
/** When {@link LANDED_TOTAL_FIELD} was last written. */
export const LANDED_COMPUTED_AT_FIELD = 'landed_computed_at';
/** The phone slider: €2 000 … €20 000 in steps of €500; the right end means "any". */
export const LANDED_FILTER_MIN_EUR = 2_000;
export const LANDED_FILTER_MAX_EUR = 20_000;
export const LANDED_FILTER_STEP_EUR = 500;
/**
 * `landedMaxEur` as it arrives in a query string: a positive whole number of
 * euros, or `null` (absent, empty, non-numeric, zero or negative — the filter
 * is then simply off; a malformed value never 400s the catalogue).
 */
export const LandedMaxEurParamSchema = z
    .string()
    .optional()
    .transform((value) => {
    if (value === undefined || value.trim() === '')
        return null;
    const parsed = Number(value.trim());
    return Number.isFinite(parsed) && parsed > 0 ? Math.floor(parsed) : null;
});
/**
 * The stamp written next to the stored landed total. The excise age
 * coefficient depends on the calendar year the estimate is made in, so the
 * year is part of the version: on January 1st every stored total is stale and
 * the recompute (`landed_rules_version != current`) picks it up.
 */
export function landedRulesVersionOf(rulesVersion, asOfYear) {
    return `${rulesVersion}@${asOfYear}`;
}
//# sourceMappingURL=catalogue-filters.js.map